import React, { useContext, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  PageWrapper,
  AccountBanner,
  AccountInfo,
  SwitchButton,
  ContentCard,
  Heading,
  Subtitle,
} from "./choose-profile.styles";
import AISetup from "./components/ai-setup";
import InitSetup from "./components/init-setup";
import { useParseResume, useGetFullPortfolio } from "../../../services/api/users-api";
import { ToastContext } from "../../../services/contexts/toast-context";
import { useUser } from "../../../services/contexts/user-context";
import { logout } from "../../../services/utils/processors";
import { LogOut } from "lucide-react";

const normalizeExperience = (exp) => {
  // Split "City, Country" location string if city/country not already separate
  let city = exp.city || "";
  let country = exp.country || "";
  if (!city && !country && exp.location) {
    const parts = exp.location.split(",").map((s) => s.trim());
    city = parts[0] || "";
    country = parts[1] || "";
  }
  return {
    ...exp,
    job_title: exp.job_title || exp.role || exp.name || "",
    city,
    country,
    description: exp.description || "",
  };
};

const normalizeCertification = (cert) => ({
  ...cert,
  title: cert.title || cert.name || "",
});

const transformGeneratedContent = (content) => {
  let result = {};

  if (Array.isArray(content)) {
    // Legacy array format: [{model: "Experience", fields: {...}}, ...]
    content.forEach((item) => {
      result[item.model] = item.fields;
    });
  } else if (content && typeof content === "object") {
    // New flat format: {"Experience": [...], "Education": [...], ...}
    result = content;
  }

  if (Array.isArray(result.Experience)) {
    result.Experience = result.Experience.map(normalizeExperience);
  }
  if (Array.isArray(result.Certification)) {
    result.Certification = result.Certification.map(normalizeCertification);
  }

  return result;
};

const ChooseProfilePage = () => {
  const toastRef = useContext(ToastContext);
  const navigate = useNavigate();
  const { user } = useUser();
  const [currentPage, setCurrentPage] = useState("init");
  const parseResumeMutation = useParseResume();
  const { data: portfolio } = useGetFullPortfolio();

  // Redirect if onboarding is already completed
  useEffect(() => {
    if (portfolio?.profile?.onboarding_step === 'completed') {
      navigate('/default/profile');
    }
  }, [portfolio, navigate]);

  const handleSwitchAccount = () => {
    logout();
    navigate("/login");
  };

  const handleManualSetup = () => {
    navigate("/onboarding/form-profile");
  };

  const handleFileUpload = (file) => {
    parseResumeMutation.mutate(file, {
      onSuccess: (response) => {
        const transformed = transformGeneratedContent(
          response.data.generated_content
        );
        localStorage.setItem("resume-info", JSON.stringify(transformed));
        navigate("/onboarding/form-profile");
        toastRef.current.show({
          severity: "success",
          summary: "Success",
          detail: "Resume parsed successfully",
          life: 3000,
        });
      },
      onError: (error) => {
        toastRef.current.show({
          severity: "error",
          summary: "Error",
          detail:
            error.response?.data?.detail ||
            error.message ||
            "Failed to parse resume",
          life: 4000,
        });
      },
    });
  };

  return (
    <PageWrapper>
      <AccountBanner>
        <AccountInfo>
          <span>Signed in as</span>
          <strong>{user?.email || "Unknown"}</strong>
        </AccountInfo>
        <SwitchButton onClick={handleSwitchAccount}>
          <LogOut size={14} />
          Switch account
        </SwitchButton>
      </AccountBanner>

      <ContentCard>
        <Heading>Let's set up your profile</Heading>
        <Subtitle>
          Choose how you'd like to get started — import your resume with AI or
          fill in your details manually.
        </Subtitle>

        {currentPage === "init" && (
          <InitSetup
            onManualSetup={handleManualSetup}
            onAISetup={() => setCurrentPage("AI")}
          />
        )}

        {currentPage === "AI" && (
          <AISetup
            onBack={() => setCurrentPage("init")}
            onFileUpload={handleFileUpload}
            onUseExisting={() => navigate("/onboarding/form-profile")}
            isUploading={parseResumeMutation.isPending}
          />
        )}
      </ContentCard>
    </PageWrapper>
  );
};

export default ChooseProfilePage;
